import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { MapContainer, TileLayer, useMap, Marker, Popup } from "react-leaflet";

/**
 * LiveWorldMap — geo-clustered map view for the Live World feed.
 *
 * Every row that has {lat,lng} is rendered as a coloured pin, then grouped
 * by leaflet.markercluster so dense cities stay readable. Tap a pin →
 * fires `onSelect(row)` so the parent can render a compact bottom-sheet
 * card. Tap a cluster → default zoom-to-bounds behaviour.
 *
 * Location privacy: Needs already ship jittered coords from the backend;
 * this component never re-projects or resolves personal identities.
 */

const KIND_STYLE = {
  need:    { bg: "#ef4444", label: "N" },
  match:   { bg: "#9333ea", label: "M" },
  offer:   { bg: "#059669", label: "O" },
  auction: { bg: "#2563eb", label: "A" },
  lead:    { bg: "#f59e0b", label: "L" },
  rate:    { bg: "#64748b", label: "R" },
};

function pinHtml(kind, price) {
  const s = KIND_STYLE[kind] || KIND_STYLE.offer;
  const money = price ? formatShort(price) : "";
  return `
    <div class="lw-pin" style="--bg:${s.bg}">
      <span class="lw-pin-label">${s.label}</span>
      ${money ? `<span class="lw-pin-price">${money}</span>` : ""}
    </div>`;
}

function formatShort(v) {
  if (v == null) return "";
  if (Math.abs(v) >= 1e7) return "₹" + (v / 1e7).toFixed(1) + "cr";
  if (Math.abs(v) >= 1e5) return "₹" + (v / 1e5).toFixed(1) + "L";
  if (Math.abs(v) >= 1e3) return "₹" + (v / 1e3).toFixed(0) + "k";
  return "₹" + v;
}

function ClusterLayer({ rows, onSelect }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    // Build a fresh cluster group when rows change.
    if (groupRef.current) {
      try { map.removeLayer(groupRef.current); } catch { /* ignore */ }
      groupRef.current = null;
    }
    const cluster = L.markerClusterGroup({
      showCoverageOnHover:  false,
      spiderfyOnMaxZoom:    true,
      disableClusteringAtZoom: 16,
      maxClusterRadius:     55,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        const size = count < 10 ? 34 : count < 50 ? 42 : count < 200 ? 52 : 60;
        return L.divIcon({
          html: `<div class="lw-cluster" style="width:${size}px;height:${size}px">${count}</div>`,
          className: "",
          iconSize: [size, size],
        });
      },
    });

    rows
      .filter((r) => typeof r.lat === "number" && typeof r.lng === "number")
      .forEach((r) => {
        const priceVal = r.price ?? r.ask ?? r.bid ?? null;
        const icon = L.divIcon({
          className: "",
          html: pinHtml(r.kind, priceVal),
          iconSize: [56, 32],
          iconAnchor: [28, 32],
        });
        const marker = L.marker([r.lat, r.lng], { icon, riseOnHover: true });
        marker.on("click", () => onSelect && onSelect(r));
        cluster.addLayer(marker);
      });

    map.addLayer(cluster);
    groupRef.current = cluster;

    // Fit bounds if we have any rows
    if (cluster.getLayers().length > 0) {
      try {
        map.fitBounds(cluster.getBounds().pad(0.15), { maxZoom: 13, animate: false });
      } catch { /* single-row edge cases */ }
    }

    return () => {
      if (groupRef.current) {
        try { map.removeLayer(groupRef.current); } catch { /* ignore */ }
        groupRef.current = null;
      }
    };
  }, [rows, map, onSelect]);

  return null;
}

function UserPin({ loc }) {
  const map = useMap();
  useEffect(() => {
    if (!loc) return undefined;
    const icon = L.divIcon({
      html: `<div class="lw-me"></div>`,
      className: "",
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });
    const m = L.marker([loc.lat, loc.lng], { icon, interactive: false, zIndexOffset: -100 });
    m.addTo(map);
    return () => { try { map.removeLayer(m); } catch { /* ignore */ } };
  }, [loc, map]);
  return null;
}

export default function LiveWorldMap({ rows, loc, onSelect, height = "60vh" }) {
  const center = useMemo(() => {
    if (loc?.lat) return [loc.lat, loc.lng];
    const first = (rows || []).find((r) => typeof r.lat === "number");
    if (first) return [first.lat, first.lng];
    return [19.076, 72.8777];
  }, [rows, loc]);

  const pinCount = (rows || []).filter((r) => typeof r.lat === "number").length;

  return (
    <div
      className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 relative"
      style={{ height }}
      data-testid="lw-map-container"
    >
      <MapContainer
        center={center}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        preferCanvas
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          attribution="© OpenStreetMap contributors"
          maxZoom={19}
        />
        <UserPin loc={loc} />
        <ClusterLayer rows={rows || []} onSelect={onSelect} />
      </MapContainer>
      <div className="absolute top-3 left-3 z-[400] bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-black text-stone-800 shadow" data-testid="lw-map-count">
        {pinCount} pin{pinCount !== 1 ? "s" : ""}
      </div>
      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur px-2 py-1.5 rounded-xl text-[10px] font-bold text-stone-700 shadow flex items-center gap-2" data-testid="lw-map-legend">
        {Object.entries(KIND_STYLE).slice(0, 5).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-0.5">
            <span className="w-2 h-2 rounded-full" style={{ background: v.bg }} />
            <span className="uppercase">{k}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
